import { useState } from 'react'
import { 
  Globe, 
  Search, 
  Filter,
  Star,
  Circle,
  Zap,
  Shield,
  Eye,
  Flag,
  Navigation
} from 'lucide-react'
import { Button } from '../components/ui/button'

interface StarSystem {
  id: string
  name: string
  type: 'habitable' | 'hostile' | 'resource' | 'empty' | 'contested'
  planets: number
  owner?: string
  distance: number
  coordinates: { x: number; y: number }
  threat: 'low' | 'medium' | 'high'
  resources: string[]
}

const GalaxyMap = () => {
  const [selectedSystem, setSelectedSystem] = useState<StarSystem | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'list' | 'map'>('map')

  const [systems] = useState<StarSystem[]>([
    {
      id: '1',
      name: 'Sol System',
      type: 'habitable',
      planets: 8,
      owner: 'Terran Federation',
      distance: 0,
      coordinates: { x: 0, y: 0 },
      threat: 'low',
      resources: ['Metal', 'Water', 'Energy']
    },
    {
      id: '2',
      name: 'Alpha Centauri',
      type: 'habitable',
      planets: 3,
      distance: 4.3,
      coordinates: { x: 150, y: 120 },
      threat: 'low',
      resources: ['Rare Metals', 'Crystals']
    },
    {
      id: '3',
      name: 'Kepler-442',
      type: 'resource',
      planets: 5,
      distance: 1200,
      coordinates: { x: 300, y: 200 },
      threat: 'medium',
      resources: ['Quantum Materials', 'Dark Matter']
    },
    {
      id: '4',
      name: 'Wolf 359',
      type: 'hostile',
      planets: 2,
      distance: 7.9,
      coordinates: { x: 80, y: 280 },
      threat: 'high',
      resources: ['Weapons Grade Materials']
    },
    {
      id: '5',
      name: 'Proxima Centauri',
      type: 'contested',
      planets: 1,
      owner: 'Disputed',
      distance: 4.2,
      coordinates: { x: 180, y: 150 },
      threat: 'high',
      resources: ['Strategic Resources']
    }
  ])

  const getSystemColor = (type: string) => {
    switch (type) {
      case 'habitable': return 'bg-green-500'
      case 'hostile': return 'bg-red-500'
      case 'resource': return 'bg-yellow-500'
      case 'contested': return 'bg-purple-500'
      case 'empty': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getSystemIcon = (type: string) => {
    switch (type) {
      case 'habitable': return Circle
      case 'hostile': return Zap
      case 'resource': return Star
      case 'contested': return Shield
      case 'empty': return Globe
      default: return Globe
    }
  }

  const getThreatColor = (threat: string) => {
    switch (threat) {
      case 'low': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'high': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  const filteredSystems = systems.filter(system => {
    const matchesSearch = system.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         system.owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         system.resources.some(r => r.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesType = typeFilter === 'all' || system.type === typeFilter
    return matchesSearch && matchesType
  })

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <Globe className="w-10 h-10 mr-3 text-purple-400" />
              Galaxy Map
            </h1>
            <p className="text-gray-400">Explore the known universe and discover new worlds</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'map' ? 'default' : 'outline'}
              onClick={() => setViewMode('map')}
            >
              <Globe className="w-4 h-4 mr-2" />
              Map View
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              onClick={() => setViewMode('list')}
            >
              <Filter className="w-4 h-4 mr-2" />
              List View
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-6 h-6 text-yellow-400" />
              <span className="text-xs text-gray-400">SYSTEMS</span>
            </div>
            <div className="text-2xl font-bold text-white">{systems.length}</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Circle className="w-6 h-6 text-green-400" />
              <span className="text-xs text-gray-400">HABITABLE</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {systems.filter(s => s.type === 'habitable').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Shield className="w-6 h-6 text-purple-400" />
              <span className="text-xs text-gray-400">CONTESTED</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {systems.filter(s => s.type === 'contested').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-6 h-6 text-red-400" />
              <span className="text-xs text-gray-400">HOSTILE</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {systems.filter(s => s.type === 'hostile').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Flag className="w-6 h-6 text-blue-400" />
              <span className="text-xs text-gray-400">CONTROLLED</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {systems.filter(s => s.owner).length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search systems, owners, or resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-8 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none min-w-[150px]"
            >
              <option value="all">All Types</option>
              <option value="habitable">Habitable</option>
              <option value="hostile">Hostile</option>
              <option value="resource">Resource Rich</option>
              <option value="contested">Contested</option>
              <option value="empty">Empty</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {viewMode === 'map' ? (
              /* Galaxy Map View */
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                <h3 className="text-xl font-bold text-white mb-4">Galactic Overview</h3>
                <div className="relative bg-slate-900/50 rounded-lg overflow-hidden" style={{ height: '500px' }}>
                  {/* Stars background */}
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(2px 2px at 20px 30px, white, transparent),
                                      radial-gradient(2px 2px at 40px 70px, white, transparent),
                                      radial-gradient(1px 1px at 90px 40px, white, transparent),
                                      radial-gradient(1px 1px at 130px 80px, white, transparent),
                                      radial-gradient(2px 2px at 160px 30px, white, transparent)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '200px 100px'
                  }}></div>
                  
                  {/* Star Systems */}
                  {filteredSystems.map((system) => {
                    const SystemIcon = getSystemIcon(system.type)
                    return (
                      <div
                        key={system.id}
                        className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 hover:scale-110 ${
                          selectedSystem?.id === system.id ? 'scale-125 z-10' : ''
                        }`}
                        style={{
                          left: `${Math.min(Math.max(system.coordinates.x, 30), 90)}%`,
                          top: `${Math.min(Math.max(system.coordinates.y, 30), 90)}%`
                        }}
                        onClick={() => setSelectedSystem(system)}
                      >
                        <div className={`w-4 h-4 ${getSystemColor(system.type)} rounded-full shadow-lg`}>
                          <div className={`w-8 h-8 -mt-2 -ml-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center border-2 ${
                            selectedSystem?.id === system.id ? 'border-purple-400' : 'border-transparent'
                          }`}>
                            <SystemIcon className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        {selectedSystem?.id === system.id && (
                          <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 border border-white/20 rounded-lg p-2 min-w-[120px] text-center">
                            <div className="text-white text-sm font-semibold">{system.name}</div>
                            <div className="text-gray-400 text-xs">{system.distance} ly</div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                
                <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
                  <div>Click on systems to view details</div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span>Habitable</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span>Resource</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span>Hostile</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <span>Contested</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                {filteredSystems.map((system) => {
                  const SystemIcon = getSystemIcon(system.type)
                  return (
                    <div
                      key={system.id}
                      className={`bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6 cursor-pointer transition-all duration-200 hover:border-purple-500/30 ${
                        selectedSystem?.id === system.id ? 'border-purple-500/50 bg-purple-900/20' : ''
                      }`}
                      onClick={() => setSelectedSystem(system)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 ${getSystemColor(system.type)} rounded-lg flex items-center justify-center`}>
                            <SystemIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">{system.name}</h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-400">
                              <span>{system.distance} light years</span>
                              <span>{system.planets} planets</span>
                              <span className={getThreatColor(system.threat)}>
                                {system.threat} threat
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-400 mb-1">Type</div>
                          <div className="text-white capitalize">{system.type}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* System Details Sidebar */}
          <div className="space-y-6">
            {selectedSystem ? (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">{selectedSystem.name}</h3>
                  <div className={`px-3 py-1 rounded-full text-sm ${getSystemColor(selectedSystem.type)} text-white`}>
                    {selectedSystem.type}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Distance</span>
                    <span className="text-white">{selectedSystem.distance} ly</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-400">Planets</span>
                    <span className="text-white">{selectedSystem.planets}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-400">Threat Level</span>
                    <span className={getThreatColor(selectedSystem.threat)}>
                      {selectedSystem.threat}
                    </span>
                  </div>
                  
                  {selectedSystem.owner && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Owner</span>
                      <span className="text-white">{selectedSystem.owner}</span>
                    </div>
                  )}
                  
                  <div>
                    <span className="text-gray-400 block mb-2">Resources</span>
                    <div className="flex flex-wrap gap-2">
                      {selectedSystem.resources.map((resource, index) => (
                        <span key={index} className="px-2 py-1 bg-slate-700/50 text-white text-xs rounded">
                          {resource}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 mt-6">
                  <Button className="w-full">
                    <Navigation className="w-4 h-4 mr-2" />
                    Send Fleet
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Search className="w-4 h-4 mr-2" />
                    Deep Scan
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6 text-center">
                <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-400 mb-2">Select a System</h3>
                <p className="text-gray-500">Click on a system to view detailed information</p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <Search className="w-4 h-4 mr-2" />
                  Launch Probe
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Navigation className="w-4 h-4 mr-2" />
                  Fleet Deployment
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Star className="w-4 h-4 mr-2" />
                  Survey Mission
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GalaxyMap